import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';

import InputField from 'components/common/InputField/InputField';
import { createEmailValidationFn } from 'utils/validations/validations';
import { NO_CACHE } from 'graphql/fetchPolicies';
import { getObjValue } from 'utils/functions';
import {
  updateTemplateTextFieldValue,
  templateStartTyping as templateStartTypingMutation,
  templateStopTyping as templateStopTypingMutation,
  getLatestAssessmentEventById
} from 'graphql/operations';
import { EMAIL_ADDRESS } from 'components/threepp/assessment/Answers/Text/InputMask';
import { useDebouncedStartTyping, useDebouncedStopTyping } from 'components/threepp/assessment/Answers/helper';
import SaveTemplateAnswerHOC from '../SaveTemplateAnswerHOC';
import TemplateFieldUpdatesHOC from '../TemplateFieldUpdatesHOC';

import './TextAnswer.scss';

const TEXT_ANSWER_CHARACTER_LIMIT = 5000; // size of the column in RDS

const getValidationFunction = mask => {
  switch (mask) {
    case EMAIL_ADDRESS:
      return createEmailValidationFn(null, true);
    default:
      return undefined;
  }
};

const setCharacterLimit = (charLength, maxLength) => {
  if (charLength && charLength < maxLength) {
    return charLength;
  }
  return maxLength;
};

export function TemplateTextAnswer(props) {
  const { save, disabled, question, latestEvent, templateId, recordId, templateStartTyping, templateStopTyping } = props;
  const { t } = useTranslation();
  const { textValue, mask, maxCharactersAllowed, calculated } = question;

  const action = getObjValue(latestEvent, 'action');
  const isDisabled = disabled || action === 'START_TYPING';

  const userStartTypingCallback = useDebouncedStartTyping(templateStartTyping, templateId, question.id, true, recordId);
  const userStopTypingCallback = useDebouncedStopTyping(templateStopTyping, templateId, question.id, true, recordId);

  const handleChangeCallback = useCallback(
    input => {
      userStartTypingCallback();
      userStopTypingCallback();
      const normalized = input === '' ? null : input;
      save(normalized);
    },
    [save, userStartTypingCallback, userStopTypingCallback]
  );

  const handleValidationErrorCallback = useCallback(() => save.cancel(), [save]);

  const minRows = mask ? 1 : 4;
  const maxRows = mask ? 1 : 16;
  const inputValidation = getValidationFunction(mask);

  return (
    <InputField
      t={t}
      name="answer"
      classes={`text-field-answer ${minRows > 1 ? 'multiline' : ''}`}
      fullWidth
      type="text"
      value={textValue}
      minRows={minRows}
      maxRows={maxRows}
      multiline={minRows > 1}
      disabled={calculated || isDisabled}
      inputValidation={inputValidation}
      onChange={handleChangeCallback}
      maxCharactersAllowed={setCharacterLimit(maxCharactersAllowed, TEXT_ANSWER_CHARACTER_LIMIT)}
      onValidationError={handleValidationErrorCallback}
    />
  );
}

TemplateTextAnswer.defaultProps = {
  latestEvent: { action: '' }
};

TemplateTextAnswer.propTypes = {
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  save: PropTypes.any.isRequired,
  disabled: PropTypes.bool.isRequired,
  templateStartTyping: PropTypes.func.isRequired,
  templateStopTyping: PropTypes.func.isRequired,
  latestEvent: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};

export const TemplateTextAnswerHOC = compose(
  withApollo,
  graphql(updateTemplateTextFieldValue, { name: 'fieldMutation' }),
  graphql(templateStartTypingMutation, { name: 'templateStartTyping' }),
  graphql(templateStopTypingMutation, { name: 'templateStopTyping' }),
  graphql(getLatestAssessmentEventById, {
    // TODO: Need to Template-ize this (getLatestTemplateEventById)
    options: props => ({
      variables: {
        fieldId: props.question.id
      },
      fetchPolicy: NO_CACHE
    }),
    name: 'getLatestAssessmentEventById'
  }),
  SaveTemplateAnswerHOC,
  TemplateFieldUpdatesHOC,
  mapProps(props => ({
    question: props.question,
    disabled: props.disabled,
    templateId: props.assessmentId,
    recordId: props.recordId,
    save: props.save,
    templateStartTyping: props.templateStartTyping,
    templateStopTyping: props.templateStopTyping,
    latestEvent: props.getLatestAssessmentEventById.getLatestAssessmentEventById
  }))
);

export default TemplateTextAnswerHOC(TemplateTextAnswer);
